import { useRef, useState } from "react";
import { uploadFile, fileSrc } from "@/lib/uploads";
import { toast } from "@/components/Toast";
import { Camera, Video, X, Loader2, FileText, Paperclip } from "lucide-react";
import { InAppCamera } from "@/components/InAppCamera";

const DOC_MIMES = "application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv";

/**
 * Multi-attachment picker. Calls onChange(list) with array of {url, content_type, kind, size, filename}.
 * Props: accept="image" | "video" | "document" | "any" (default "any"), multiple (default true), max (default 4)
 */
export function AttachmentPicker({ value = [], onChange, accept = "any", multiple = true, max = 4, label = "Adjuntar fotos/video" }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [inAppCam, setInAppCam] = useState(false);

  const acceptMime =
    accept === "image" ? "image/*" :
    accept === "video" ? "video/*" :
    accept === "document" ? DOC_MIMES :
    accept === "document_video" ? `video/mp4,${DOC_MIMES}` :
    accept === "image_video" ? "image/*,video/*" :
    accept === "image_document" ? `image/*,application/pdf` :
    `image/*,video/mp4,${DOC_MIMES}`;

  // La cámara en el momento solo aplica cuando se pueden adjuntar fotos/video
  // (no tiene sentido "tomar foto" para subir una planilla PDF).
  const showCamera = accept === "image" || accept === "video" || accept === "image_video" || accept === "image_document" || accept === "any";

  const onPick = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (files.length) await uploadList(files);
    input.value = "";
  };

  // Fix BO (2026-11-03): foto con la cámara DENTRO de la app (ver InAppCamera). Para video, o si el
  // navegador no tiene getUserMedia, se usa la cámara del celular como antes.
  const canInApp = accept !== "video" && typeof navigator !== "undefined" && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  const openCamera = () => { if (canInApp) setInAppCam(true); else cameraRef.current?.click(); };

  const uploadList = async (files) => {
    if (value.length + files.length > max) {
      toast.error(`Máximo ${max} archivos`);
      return;
    }
    setBusy(true);
    const next = [...value];
    for (const f of files) {
      try {
        const r = await uploadFile(f);
        next.push({ url: r.url, content_type: r.content_type, kind: r.kind, size: r.size, filename: f.name });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Error al subir " + f.name);
      }
    }
    onChange(next);
    setBusy(false);
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  const Ic = accept === "document" || accept === "document_video" ? Paperclip : Camera;

  const btnStyle = {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 14px", border: "2px dashed var(--invict-bone-3)",
    background: "var(--invict-bone-2)", color: "#0A0A0A",
    fontWeight: 700, fontSize: 12, borderRadius: 3, cursor: "pointer",
    textTransform: "uppercase", letterSpacing: "0.05em",
  };

  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--invict-text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label} ({value.length}/{max})
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={acceptMime}
        multiple={multiple}
        onChange={onPick}
        style={{ display: "none" }}
        data-testid="attach-input"
      />
      {showCamera && (
        <input
          ref={cameraRef}
          type="file"
          accept={acceptMime}
          capture="environment"
          onChange={onPick}
          style={{ display: "none" }}
          data-testid="attach-camera-input"
        />
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {showCamera && (
          <button
            type="button"
            onClick={openCamera}
            disabled={busy || value.length >= max}
            data-testid="attach-camera-btn"
            style={btnStyle}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Camera size={14} />}
            {busy ? "Subiendo..." : "Tomar foto"}
          </button>
        )}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || value.length >= max}
          data-testid="attach-btn"
          style={btnStyle}
        >
          {busy ? <Loader2 size={14} className="spin" /> : <Ic size={14} />}
          {busy ? "Subiendo..." : showCamera ? "Elegir archivo" : `${label} (${value.length}/${max})`}
        </button>
      </div>
      {inAppCam && (
        <InAppCamera
          onCapture={async (file) => { await uploadList([file]); setInAppCam(false); }}
          onFallback={() => { setInAppCam(false); cameraRef.current?.click(); }}
          onClose={() => setInAppCam(false)}
        />
      )}
      {value.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8, marginTop: 10 }}>
          {value.map((a, i) => (
            <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 3, overflow: "hidden", background: "#000" }}>
              {a.kind === "image" ? (
                <img src={fileSrc(a.url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : a.kind === "video" ? (
                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#FFFBF4", gap: 4 }}>
                  <Video size={20} />
                  <div style={{ fontSize: 9, letterSpacing: "0.1em" }}>VIDEO</div>
                </div>
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#FFFBF4", gap: 4, padding: 4, textAlign: "center" }}>
                  <FileText size={20} />
                  <div style={{ fontSize: 9, letterSpacing: "0.05em", wordBreak: "break-all", lineHeight: 1.1 }}>{(a.filename || "DOC").slice(0, 18)}</div>
                </div>
              )}
              <button type="button" onClick={() => remove(i)} data-testid={`attach-rm-${i}`} style={{
                position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.7)", color: "#fff",
                border: "none", borderRadius: "50%", width: 22, height: 22, display: "flex",
                alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Componente para mostrar attachments en modo solo lectura */
export function AttachmentGrid({ items = [] }) {
  if (!items.length) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: 8, marginTop: 10 }}>
      {items.map((a, i) => (
        <a key={i} href={fileSrc(a.url)} target="_blank" rel="noreferrer" style={{ aspectRatio: "1", borderRadius: 3, overflow: "hidden", background: "#000", display: "block" }}>
          {a.kind === "image" ? (
            <img src={fileSrc(a.url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#FFFBF4", gap: 4 }}>
              <Video size={20} />
              <div style={{ fontSize: 9, letterSpacing: "0.1em" }}>VIDEO</div>
            </div>
          )}
        </a>
      ))}
    </div>
  );
}
